import { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Input, Modal, Steps } from 'antd';
import { filter, map } from 'lodash';
import moment from 'moment';
import { TOrder } from 'types/Orders/order';
import { TCustomer } from 'types/customer';

import { OrderResp } from '@api/responseModels/order/orderResponse';
import { Loader } from '@atoms/loader';
import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import { SettingsManager } from '@classes/settingsManager';

import { GlobalConstants } from '@constants/global';
import { debounce } from '@utils/functions';

import { CustomerStep } from './steps/customerStep';
import { OrderObjectStep } from './steps/orderObjectStep';
import { StartDateStep } from './steps/startDateStep';

import '@organisms/orders/common/pickerModal/pickerModal.scss';

const { Search } = Input;

/**
 * Клиент, на которого оформляют заказ: при нём шаг выбора клиента пропускается.
 *
 * Полный профиль здесь не нужен — в ход идёт только идентификатор, поэтому
 * годится и урезанный клиент из самого заказа (`TOrder['customer']`), у
 * которого остальных полей попросту нет.
 */
export type TOrderCustomer = Pick<TCustomer, 'id'> & Partial<TCustomer>;

type TProps = {
    isModalOpen: boolean;
    setIsModalOpen: (value: boolean) => void;
    customer?: TOrderCustomer;
    setOrderData?: React.Dispatch<React.SetStateAction<TOrder>>;
    /** Заказ создан: наружу отдаём его целиком — по нему открывают карточку заказа. */
    onCreateOrder?: (order: TOrder) => void;
    onOpen?: () => void;
    entities?: {
        type: 'product' | 'service' | 'task';
        id: number;
        price: number;
        quantity: number;
    }[];
    createCustomerIfNotExist?: boolean;
};

export const CreateOrderModal = ({
    isModalOpen,
    setIsModalOpen,
    customer = null,
    setOrderData,
    onCreateOrder,
    onOpen,
    entities = [],
    createCustomerIfNotExist,
}: TProps) => {
    const [current, setCurrent] = useState(customer ? 1 : 0);
    const [isLoading, setIsLoading] = useState(false);
    /** Поиск переехал в шапку модалки, поэтому его состояние живёт здесь. */
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    /** На вкладке создания объекта «Далее» сначала сохраняет объект. */
    const [isObjectCreationTab, setIsObjectCreationTab] = useState(false);

    const childRef = useRef<{ doCreate: () => Promise<void> } | null>(null);
    const [selectedOrderCustomer, setSelectedOrderCustomer] =
        useState<TOrderCustomer>(customer);
    const [orderObjectId, setOrderObjectId] = useState<number>(null);
    const [startDate, setStartDate] = useState<moment.Moment | null>(
        moment().local().add(30, 'minutes')
    );
    const [finishDate, setFinishDate] = useState<moment.Moment | null>(null);
    // Окончание необязательно, но если выбрано — не раньше начала: иначе бэк вернёт 422
    const isDateRangeValid = !finishDate || finishDate.isAfter(startDate);

    const creds = SettingsManager.getConnectionCredentials();

    useEffect(() => {
        if (customer) {
            setSelectedOrderCustomer(customer);
            setCurrent(1);
        }
    }, [customer]);
    const steps = [
        {
            title: 'Клиент',
            content: (
                <CustomerStep
                    setSelectedOrderCustomer={setSelectedOrderCustomer}
                    selectedOrderCustomer={selectedOrderCustomer}
                    setCurrentStep={setCurrent}
                    createCustomerIfNotExist={createCustomerIfNotExist}
                    searchQuery={searchQuery}
                />
            ),
        },
        {
            title: 'Объект',
            content: (
                <OrderObjectStep
                    orderObjectId={orderObjectId}
                    setOrderObjectId={setOrderObjectId}
                    customerId={selectedOrderCustomer?.id}
                    setCurrentStep={setCurrent}
                    setIsObjectCreationTab={setIsObjectCreationTab}
                    ref={childRef}
                />
            ),
        },
        {
            title: 'Дата начала',
            content: (
                <StartDateStep
                    startDate={startDate}
                    setStartDate={setStartDate}
                    finishDate={finishDate}
                    setFinishDate={setFinishDate}
                />
            ),
        },
    ];

    const applySearch = useMemo(
        () => debounce((value: unknown) => setSearchQuery(String(value)), GlobalConstants.SearchTimeout),
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        applySearch(value);
    };

    const next = () => {
        setCurrent(current + 1);
    };

    const handleCreateOrderObject = async () => {
        if (childRef.current) {
            await childRef.current?.doCreate();
            next();
        }
    };

    const prev = () => {
        setCurrent(current - 1);
    };

    const handlePickerCancel = () => {
        setSelectedOrderCustomer(null);
        setCurrent(0);
        setStartDate(moment().add(1, 'hour'));
        setFinishDate(null);
        setIsModalOpen(false);
    };

    const handlePickerOK = () => {
        setIsModalOpen(false);
    };

    const getEntitiesByType = (type: 'product' | 'service' | 'task') =>
        map(
            filter(entities, (entity) => entity.type === type),
            (entity) => {
                return {
                    id: entity.id,
                    price: entity.price,
                    quantity: entity.quantity || 1,
                };
            }
        );

    const handleCreateOrder = async () => {
        setIsLoading(true);
        try {
            const torder = await CRMAPIManager.request<OrderResp>(async (api) => {
                return await api.saveOrder(
                    creds?.crmID,
                    getEntitiesByType('product'),
                    getEntitiesByType('service'),
                    selectedOrderCustomer?.id,
                    moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
                    orderObjectId || undefined,
                    undefined,
                    finishDate?.format('YYYY-MM-DD HH:mm:ss')
                );
            });
            if (torder.errorMessages) throw torder.errorMessages;

            const createdOrder = torder.data.data;
            setOrderData && setOrderData(createdOrder);

            messageService.sendSuccess('Заказ создан!');
            setIsModalOpen(false);
            onCreateOrder && onCreateOrder(createdOrder);
        } catch (errors) {
            messageService.sendErrorList(errors);
        }
        setIsLoading(false);
    };

    const handleContinueWithoutObject = () => {
        setOrderObjectId(null);
        next();
    };

    useEffect(() => {
        if (onOpen && isModalOpen) onOpen();
    }, [isModalOpen]);

    const items = steps.map((item) => ({ key: item.title, title: item.title }));
    return (
        <Modal
            footer={
                <div className="picker-modal__footer">
                    {/* На шаге клиента кнопок нет: галочка в карточке и выбирает,
                        и переводит дальше. На шаге объекта «Далее» остаётся и
                        включается, когда объект выбран */}
                    {current === 1 && (
                        <>
                            <Button type="default" onClick={handleContinueWithoutObject}>
                                Без объекта
                            </Button>
                            <Button type="default" onClick={() => prev()}>
                                Назад
                            </Button>
                            <Button
                                type="primary"
                                onClick={
                                    isObjectCreationTab ? handleCreateOrderObject : () => next()
                                }
                            >
                                Далее
                            </Button>
                        </>
                    )}
                    {current === steps.length - 1 && (
                        <>
                            <Button type="default" onClick={() => prev()}>
                                Назад
                            </Button>
                            <Button
                                type="primary"
                                disabled={!isDateRangeValid}
                                onClick={() => handleCreateOrder()}
                            >
                                Создать заказ
                            </Button>
                        </>
                    )}
                </div>
            }
            zIndex={899}
            className="product-category-picker picker-modal"
            title={
                <div className="picker-modal__title">
                    <span>Создание заказа</span>
                    {/* Поиск в шапке: внутри шага он занимал место списка */}
                    {current === 0 && (
                        <Search
                            allowClear
                            className="header-search-input"
                            placeholder="Поиск"
                            value={searchInput}
                            onChange={(event) => handleSearchChange(event.target.value)}
                        />
                    )}
                </div>
            }
            open={isModalOpen}
            onOk={handlePickerOK}
            onCancel={handlePickerCancel}
            cancelText="Отмена"
            wrapClassName="sticky-modal"
        >
            <>
                {isLoading && <Loader />}
                <Steps
                    direction="horizontal"
                    responsive={false}
                    size="small"
                    current={current}
                    items={items}
                />
                <div className="picker-modal__step">{steps[current].content}</div>
            </>
        </Modal>
    );
};
